/**
 * SSE hook for real-time document list updates.
 *
 * Replaces polling (refetchInterval) with Server-Sent Events.
 * When a document changes on the server, this hook invalidates
 * the TanStack Query cache so the UI updates automatically.
 *
 * Auth: Passes the Office access token as ?token=ot_xxx query param
 * since EventSource does not support custom headers.
 */

import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { API_V1 } from '../config'
import { devLog } from '../services/devLog'

const RECONNECT_DELAY = 5_000 // 5 seconds
const MAX_RECONNECT_DELAY = 60_000 // 1 minute

interface UseDocumentListSSEOptions {
  /** Access token (ot_xxx) for auth */
  token: string | null
  /** Set to false to disable the connection */
  enabled?: boolean
}

export function useDocumentListSSE({ token, enabled = true }: UseDocumentListSSEOptions) {
  const queryClient = useQueryClient()
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(RECONNECT_DELAY)

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (!enabled || !token) return

    cleanup()

    const url = `${API_V1}/documents/events/?token=${encodeURIComponent(token)}`
    devLog.info('SSE', 'Connecting to document list events...')

    try {
      const es = new EventSource(url)
      eventSourceRef.current = es

      es.onopen = () => {
        devLog.info('SSE', 'Connected')
        reconnectDelayRef.current = RECONNECT_DELAY // Reset on success
      }

      es.onerror = () => {
        devLog.warn('SSE', 'Connection error — will reconnect')
        es.close()
        eventSourceRef.current = null

        // Exponential backoff
        const delay = reconnectDelayRef.current
        reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY)

        reconnectTimeoutRef.current = setTimeout(() => {
          devLog.info('SSE', `Reconnecting (delay: ${delay}ms)`)
          connect()
        }, delay)
      }

      // Handle all document events — just invalidate the query cache
      const handleEvent = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data)
          devLog.info('SSE', `${event.type}: ${data.document_id} → ${data.status}`)

          // Invalidate document list and the specific document
          queryClient.invalidateQueries({ queryKey: ['documents'] })
          if (data.document_id) {
            queryClient.invalidateQueries({ queryKey: ['document', data.document_id] })
          }
        } catch {
          devLog.warn('SSE', 'Failed to parse event')
        }
      }

      es.addEventListener('document_created', handleEvent)
      es.addEventListener('document_status_changed', handleEvent)
      es.addEventListener('document_deleted', handleEvent)
    } catch {
      devLog.warn('SSE', 'Failed to create EventSource')
    }
  }, [enabled, token, cleanup, queryClient])

  useEffect(() => {
    connect()
    return cleanup
  }, [connect, cleanup])
}
